import type {DocumentType} from '@aws-amplify/core/internals/utils';
import type {AssistantLocale} from '@perfectpost/perfect-post-common';
import {get, post, put, del, patch} from 'aws-amplify/api';
import {fetchAuthSession} from 'aws-amplify/auth';

type RestVerb = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

function toPlainHeaders(h: Headers): Record<string, string> {
  return Object.fromEntries(h.entries());
}

// Provider par défaut : Récupère le token depuis Amplify Auth (Session locale)
async function defaultTokenProvider(): Promise<string | undefined> {
  try {
    const s = await fetchAuthSession();
    return s.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get Cognito ID token:', error);
    return undefined;
  }
}

// --- Types minimaux pour reconnaître la réponse/erreur Amplify Gen2 ---
type AmplifyBody = {
  text: () => Promise<string>;
  json: () => Promise<unknown>;
};

type AmplifyReadableBody = AmplifyBody & ReadableStream<Uint8Array>;

type AmplifyResponse = {
  statusCode?: number;
  headers?: Record<string, string>;
  body: AmplifyBody;
};

type AmplifyRestErrorShape = {
  response?: AmplifyResponse;
};

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (!isRecord(v)) {
    return false;
  }

  const proto = Object.getPrototypeOf(v) as object | null;
  return proto === Object.prototype || proto === null;
}

async function extractRequestBody(request: Request): Promise<DocumentType | undefined> {
  let cloned: Request;

  try {
    cloned = request.clone();
  } catch {
    return undefined;
  }

  const contentType = cloned.headers.get('content-type')?.toLowerCase() ?? '';

  if (contentType.includes('application/json')) {
    try {
      return (await cloned.json()) as DocumentType;
    } catch {
      // fallback vers text() plus bas
    }
  }

  if (contentType.includes('multipart/form-data')) {
    try {
      return (await cloned.formData()) as unknown as DocumentType;
    } catch {
      // fallback vers text() plus bas
    }
  }

  if (contentType.includes('application/x-www-form-urlencoded')) {
    try {
      const text = await cloned.text();
      return new URLSearchParams(text) as unknown as DocumentType;
    } catch {
      return undefined;
    }
  }

  try {
    const text = await cloned.text();

    if (!text) {
      return undefined;
    }

    const jsonBody = parseJsonString(text);

    if (typeof jsonBody !== 'undefined') {
      return jsonBody as DocumentType;
    }

    return text;
  } catch {
    return undefined;
  }
}

function parseJsonString(value: string): unknown {
  const trimmed = value.trim();

  if (!trimmed) {
    return undefined;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

function isReadableStreamBody(body: AmplifyBody): body is AmplifyReadableBody {
  return typeof (body as {getReader?: unknown}).getReader === 'function';
}

function isAmplifyResponse(v: unknown): v is AmplifyResponse {
  return (
    isRecord(v) &&
    ('statusCode' in v
      ? typeof (v as {statusCode?: unknown}).statusCode === 'number' ||
        typeof (v as {statusCode?: unknown}).statusCode === 'undefined'
      : true) &&
    ('headers' in v ? isRecord((v as {headers?: unknown}).headers) : true) &&
    'body' in v
  );
}

function isAmplifyRestError(e: unknown): e is AmplifyRestErrorShape {
  return isRecord(e) && 'response' in e && isAmplifyResponse((e as {response: unknown}).response);
}

// --- Fonction Principale ---

export type ChatKitMode = 'creation' | 'review';

/** Opérations ChatKit qui portent un message utilisateur — les seules à devoir attendre la
 *  synchronisation. Les opérations techniques (retour de client tool, listes) passent
 *  directement : les retarder n'apporterait rien et bloquerait l'application du patch. */
const USER_MESSAGE_OPS = new Set(['threads.create', 'threads.add_user_message']);

export function makeAmplifyFetch({
  apiName,
  getDraftId,
  getMode,
  getLocale,
  ensureDraftSynced,
  tokenProvider,
}: {
  apiName: string;
  /**
   * Lu À CHAQUE REQUÊTE, pas capturé à la construction.
   *
   * Le `draftId` peut apparaître EN COURS de conversation : on génère un post sans
   * brouillon, le patch est appliqué, l'autosave crée le draft. Passer une valeur figée
   * enverrait ensuite l'ancien `draftId` (`undefined`), et le message suivant repartirait
   * en création au lieu de critiquer le draft fraîchement sauvegardé.
   *
   * Un getter permet à l'appelant de mémoïser sa config ChatKit avec des dépendances vides
   * — identité stable, donc pas de réinitialisation du widget — tout en envoyant la valeur
   * fraîche à chaque appel.
   */
  getDraftId?: () => string | undefined;
  /**
   * Parcours choisi explicitement par l'utilisateur, court-circuite le triage serveur.
   * Retourner `undefined` pour que le backend décide seul (comportement historique).
   * Getter pour la même raison que `getDraftId`.
   */
  getMode?: () => ChatKitMode | undefined;
  /**
   * Garantit que l'éditeur est synchronisé AVANT d'envoyer un message utilisateur, et
   * retourne l'identifiant de brouillon à jour.
   *
   * Le clic sur un prompt de l'écran d'accueil ChatKit passe par ce fetch comme n'importe
   * quel message : c'est donc ici, et pas dans un bouton hors du chat, que le gate a sa
   * place — il fonctionne pour tous les clients sans que ChatKit ait à exposer de callback.
   *
   * Contenu non sauvegardé => déclenche la sauvegarde existante et attend. Éditeur propre ou
   * vierge => no-op (on ne crée jamais de brouillon fantôme). Un échec fait échouer la
   * requête plutôt que d'envoyer sans `draftId` : sans brouillon connu du backend, le patch
   * serait calculé depuis une chaîne vide et viendrait se concaténer au texte existant.
   *
   * Retourne l'identifiant plutôt que `void` : `setDraft` de React est asynchrone, la ref
   * du composant ne serait pas encore à jour au retour de l'await.
   */
  /**
   * Langue dans laquelle l'assistant doit répondre, posée en `Accept-Language`.
   *
   * C'est le SEUL canal : le service ChatKit lit cet en-tête et rien d'autre
   * (`chatkit/src/app.py`), et `ChatKitOptions.locale` ne pilote que l'interface du
   * widget — il ne part pas sur le réseau. Sans ce getter, le navigateur posait sa
   * propre langue : l'extension en allemand sur un Chrome en anglais obtenait un
   * assistant anglophone.
   *
   * Getter pour la même raison que `getDraftId` : la langue arrive de l'extension APRÈS
   * le montage, et la config `api` est mémoïsée à dépendances vides.
   */
  getLocale?: () => AssistantLocale | undefined;
  ensureDraftSynced?: () => Promise<string | undefined>;
  tokenProvider?: () => Promise<string | null | undefined>;
}) {
  return async function amplifyFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    // 1. Normalisation de l'URL et de la méthode
    // On évite de créer un "new Request()" complet pour ne pas verrouiller le body stream sur Firefox
    const urlStr = input instanceof Request ? input.url : input.toString();
    const url = new URL(urlStr, window.location.origin); // safe absolute URL construction
    const path = (url.pathname.startsWith('/') ? url.pathname.slice(1) : url.pathname) || '';
    const queryParams = Object.fromEntries(url.searchParams.entries());

    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase() as RestVerb;

    // 2. Gestion des Headers
    const inputHeaders = init?.headers || (input instanceof Request ? input.headers : {});
    const headers = new Headers(inputHeaders);
    const plainHeaders: Record<string, string> = toPlainHeaders(headers);

    // 3. Authentification (Factorisée)
    const getToken = tokenProvider || defaultTokenProvider;
    const bearer = await getToken();

    if (bearer && !plainHeaders.Authorization) {
      plainHeaders.Authorization = `Bearer ${bearer}`;
    }

    // En-tête « CORS-safelisted » : aucun preflight supplémentaire. Clé en minuscules,
    // comme les sort `toPlainHeaders` : `Headers` normalise les noms, et une variante
    // capitalisée ferait DEUX entrées pour le même en-tête.
    const locale = getLocale?.();

    if (locale !== undefined && !headers.has('accept-language')) {
      plainHeaders['accept-language'] = locale;
    }

    // 4. Extraction et Fusion du Body (CORRECTIF CRITIQUE)
    let body: DocumentType | undefined = undefined;

    if (method !== 'GET') {
      // Priorité 1 : Le body est dans 'init' (cas standard fetch)
      if (init?.body != null) {
        if (typeof init.body === 'string') {
          try {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- IGNORE ---
            body = JSON.parse(init.body);
          } catch {
            body = init.body; // Si c'est pas du JSON, on le passe tel quel
          }
        } else {
          // Cas rare : FormData, Blob, etc.
          body = init.body as unknown as DocumentType;
        }
      }
      // Priorité 2 : Le body est dans l'objet Request (cas rare avec useChatKit)
      else if (input instanceof Request && input.body) {
        body = await extractRequestBody(input);
      }
    }

    // 4 bis. Gate de synchronisation
    // Avant tout message utilisateur, on s'assure que l'éditeur est enregistré, pour que le
    // backend connaisse le vrai contenu de départ et produise un diff de remplacement.
    let syncedDraftId: string | undefined;
    if (ensureDraftSynced && isPlainObject(body) && USER_MESSAGE_OPS.has(String(body.type))) {
      syncedDraftId = await ensureDraftSynced();
    }

    // 5. Injection du draftId
    // Résolu MAINTENANT, à l'instant de la requête (cf. `getDraftId`).
    const draftId = syncedDraftId ?? getDraftId?.();

    if (draftId) {
      if (isPlainObject(body)) {
        // Cas normal : on fusionne draftId dans l'objet JSON existant
        body = {...body, draftId};
      } else if (body instanceof FormData) {
        body.set('draftId', draftId);
      } else if (body instanceof URLSearchParams) {
        body.set('draftId', draftId);
      } else if (body === undefined && ['POST', 'PUT', 'PATCH'].includes(method)) {
        // Cas body vide : on crée l'objet avec juste draftId
        // Cela corrige aussi le bug du "Content-Length: 0"
        body = {draftId};
      }
    } else if (body === undefined && ['POST', 'PUT', 'PATCH'].includes(method)) {
      // Sécurité : toujours envoyer un objet vide minimum pour éviter le crash API 500
      body = {};
    }

    // 5 bis. Injection du mode
    // Même mécanique que le draftId. Le backend lit `body.mode` (cas 0 de resolve_mode) et
    // saute son appel LLM de triage. Absent => triage serveur, comme avant.
    const mode = getMode?.();

    if (mode) {
      if (isPlainObject(body)) {
        body = {...body, mode};
      } else if (body instanceof FormData || body instanceof URLSearchParams) {
        body.set('mode', mode);
      }
    }

    console.log('[AmplifyFetch]', {apiName, path, method, bodyPreview: body});

    // 6. Exécution via Amplify
    const options = {
      headers: plainHeaders,
      queryParams: Object.keys(queryParams).length ? queryParams : undefined,
      body,
    };

    const run = () => {
      switch (method) {
        case 'GET':
          return get({apiName, path, options});
        case 'POST':
          return post({apiName, path, options});
        case 'PUT':
          return put({apiName, path, options});
        case 'DELETE':
          return del({apiName, path, options});
        case 'PATCH':
          return patch({apiName, path, options});
        default:
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- IGNORE ---
          throw new Error(`Method ${method} not supported`);
      }
    };

    try {
      const restOp = run();
      const {body: respBody, statusCode, headers: respHeaders} = await restOp.response;

      if (isReadableStreamBody(respBody)) {
        return new Response(respBody, {
          status: statusCode ?? 200,
          headers: (respHeaders ?? {}) as HeadersInit,
        });
      }

      // Conversion de la réponse en texte ou JSON string
      let text: string;
      try {
        text = await respBody.text();
      } catch {
        const json = await respBody.json();
        text = JSON.stringify(json);
      }

      return new Response(text, {
        status: statusCode ?? 200,
        headers: (respHeaders ?? {}) as HeadersInit,
      });
    } catch (err: unknown) {
      // Gestion d'erreur Amplify
      if (isAmplifyRestError(err)) {
        const status = err.response?.statusCode ?? 500;
        let text: string | undefined = 'Internal Error';
        try {
          text = await err.response?.body.text();
        } catch {
          try {
            const j = await err.response?.body.json();
            text = JSON.stringify(j);
          } catch {
            // garde text par défaut
          }
        }
        return new Response(text, {status});
      }
      console.error('[AmplifyFetch] Error:', err);
      return new Response(JSON.stringify({error: 'Network or Internal Error'}), {status: 500});
    }
  };
}
